import React, { useEffect } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import "./ProgramsBanner.css";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
 
// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};
 
const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
// Renamed component
const RenewableEnergy = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    "Bio-Gas Plant Installation",
    "Solar Light & SPV System Promotion",
    "Fuel-Efficient Stoves",
    "Windmill Projects",
    "Rural Energy Planning & Management (NREP)",
  ];
 
  const { openDonation } = useDonation();
  const navigate = useNavigate();
 
  useEffect(() => {
    const els = document.querySelectorAll('.re-animate');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in-view');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.18 });
    els.forEach((el) => io.observe(el));
 
    const counters = document.querySelectorAll('.re-stat .num');
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = +el.getAttribute('data-target') || 0;
        const duration = 1400;
        const start = performance.now();
        function step(ts) {
          const t = Math.min(1, (ts - start) / duration);
          const eased = t * (2 - t);
          el.textContent = Math.floor(target * eased);
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = target + (target > 999 ? '+' : '');
        }
        requestAnimationFrame(step);
        counterObserver.unobserve(el);
      });
    }, { threshold: 0.6 });
    counters.forEach((c) => counterObserver.observe(c));
 
    return () => { io.disconnect(); counterObserver.disconnect(); };
  }, []);
 
  return (
    <>
      <Header />
 
      {/* Hero Section */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Renewable-Energy.png)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Renewable Energy
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash supports renewable energy initiatives by promoting sustainable solutions that reduce environmental impact and improve access to clean energy.
            </motion.p>
          </div>
        </Container>
      </section>
 
      {/* Redesigned middle section: About, Programs, Impact, CTA (scoped styles below) */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');
        :root{--green-1:#0f7a3a;--green-2:#1f8f4d;--accent:#eaf7ef;--card-radius:20px;--max-width:1200px;--gap:28px}
        .re-new-middle{max-width:var(--max-width);margin:0 auto;padding:0 15px}
        .re-section{padding:80px 0}
 
        /* SVG wave top */
        .re-wave{display:block;width:100%;height:46px;overflow:hidden}
        .re-wave svg{display:block;width:100%;height:100%}
 
        /* About area */
        .re-about{position:relative;background:linear-gradient(180deg,rgba(234,247,239,0.34),transparent);border-radius:28px;padding:56px;margin-bottom:56px;backdrop-filter:blur(2px)}
        .re-about:before{content:'';position:absolute;inset:0;border-radius:28px;background-image:radial-gradient(rgba(15,122,58,0.02) 1px, transparent 1px);background-size:20px 20px;pointer-events:none}
        .re-about-inner{display:flex;gap:36px;align-items:center;flex-wrap:wrap;position:relative;z-index:2}
        .re-about-left{flex:1 1 540px;min-width:260px}
        .re-badge{display:inline-block;background:linear-gradient(90deg,var(--green-2),var(--green-1));color:#fff;padding:8px 14px;border-radius:999px;font-weight:700;margin-bottom:18px;font-family:'Poppins',sans-serif;font-size:13px;letter-spacing:0.2px}
        .re-about h2{font-family:'Poppins',sans-serif;color:var(--green-1);font-weight:700;margin:6px 0 18px 0;font-size:34px}
        .re-about-card{background:linear-gradient(180deg, rgba(255,255,255,0.88), rgba(250,255,250,0.86));border-radius:var(--card-radius);padding:32px 36px;border-left:8px solid rgba(31,142,77,0.12);box-shadow:0 14px 40px rgba(9,30,15,0.06);position:relative;overflow:visible}
        .re-accent-line{position:absolute;left:0;top:0;bottom:0;width:8px;background:linear-gradient(180deg,var(--green-2),var(--green-1));border-top-left-radius:var(--card-radius);border-bottom-left-radius:var(--card-radius)}
        .re-about-card p{margin:0;color:#25332a;line-height:1.9;font-size:16.5px;font-family:Inter, Poppins, sans-serif}
        .re-about-right{flex:0 0 460px;display:flex;justify-content:center;align-items:center}
        .re-img-card{max-width:460px;border-radius:24px;overflow:hidden;background:linear-gradient(180deg,rgba(255,255,255,0.45),rgba(234,247,239,0.35));box-shadow:0 26px 60px rgba(10,40,20,0.08);transition:transform 420ms cubic-bezier(.2,.9,.2,1),box-shadow 420ms}
        .re-img-card img{width:100%;display:block;height:auto;object-fit:cover}
        .re-img-card:hover{transform:translateY(-10px) rotate(-0.3deg) scale(1.01);box-shadow:0 40px 90px rgba(11,90,40,0.12)}
 
        /* Programs grid - premium cards */
        .re-programs{padding:28px 0 0}
        .re-programs .section-header{text-align:center;margin-bottom:20px;color:var(--green-1);font-family:'Poppins',sans-serif}
        .re-programs-grid{display:flex;flex-wrap:wrap;gap:22px;justify-content:center}
        .re-card{background:linear-gradient(180deg,#ffffff 0%, rgba(234,247,239,0.5) 100%);border-radius:20px;padding:28px 20px;text-align:center;min-height:220px;flex:1 1 calc(33.333% - 24px);max-width:360px;border:1px solid rgba(31,142,77,0.06);position:relative;display:flex;flex-direction:column;align-items:center;transition:transform 360ms ease, box-shadow 360ms ease}
        .re-card:nth-child(odd){background:linear-gradient(180deg,#fff 0%, rgba(231,253,239,0.4) 100%)}
        .re-card:before{content:'';position:absolute;inset:-2px;border-radius:20px;background:linear-gradient(90deg,var(--green-2),var(--green-1));filter:blur(10px);opacity:0.07;pointer-events:none}
        .re-card:after{content:'';position:absolute;left:0;right:0;bottom:0;height:10px;border-radius:0 0 18px 18px;background:linear-gradient(90deg, rgba(31,142,77,0.03), rgba(15,122,58,0.03));pointer-events:none}
        .re-icon{width:92px;height:92px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:linear-gradient(180deg, rgba(31,142,77,0.1), rgba(15,122,58,0.06));margin-top:-46px;margin-bottom:12px;box-shadow:0 12px 30px rgba(15,122,58,0.06)}
        .re-icon-inner{width:60px;height:60px;border-radius:50%;background:linear-gradient(135deg,var(--green-2),var(--green-1));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-family:'Poppins',sans-serif;transform-origin:center;transition:transform 420ms}
        .re-card h5{font-weight:700;margin:12px 0 6px 0;color:#07371e;font-family:'Poppins',sans-serif}
        .re-divider{width:56px;height:4px;border-radius:3px;background:linear-gradient(90deg,var(--green-2),var(--green-1));margin:8px auto 12px auto}
        .re-learn{margin-top:auto;color:var(--green-1);font-weight:700;font-size:14px}
        .re-card:hover{transform:translateY(-14px) rotate(-0.3deg);box-shadow:0 40px 90px rgba(12,80,35,0.12)}
        .re-card:hover .re-icon-inner{transform:scale(1.06) rotate(-6deg)}
 
        @media (max-width:991.98px){.re-card{flex:1 1 calc(50% - 24px)} .re-about-inner{flex-direction:column-reverse;align-items:stretch} .re-about-right{justify-content:flex-start}}
        @media (max-width:575.98px){.re-card{flex:1 1 100%}.re-about{padding:28px}.re-about h2{font-size:22px}.re-img-card{max-width:100%}}
 
        /* Impact strip */
        .re-impact{background:linear-gradient(180deg,rgba(234,247,239,0.9),rgba(255,255,255,0.95));padding:48px 0;border-radius:18px;margin:56px 0}
        .re-impact-inner{display:flex;gap:18px;flex-wrap:wrap;justify-content:center;align-items:stretch;max-width:var(--max-width);margin:0 auto;padding:8px}
        .re-stat{background:#fff;border-radius:16px;padding:22px 26px;min-width:180px;flex:1 1 220px;text-align:center;box-shadow:0 16px 40px rgba(15,122,58,0.06);position:relative;transition:transform 360ms}
        .re-stat:hover{transform:translateY(-6px)}
        .re-stat .num{font-family:'Poppins',sans-serif;font-weight:800;color:var(--green-1);font-size:30px;margin-bottom:6px}
        .re-stat p{margin:0;color:#254234;font-weight:700;font-family:Inter, Poppins, sans-serif;font-size:14px}
        .re-stat .stat-icon{position:absolute;top:12px;right:12px;opacity:0.12;font-size:28px;color:var(--green-1)}
 
        /* CTA */
        .re-cta{background:linear-gradient(135deg,var(--green-2),var(--green-1));border-radius:30px;padding:64px 28px;color:#fff;margin-top:40px;position:relative;overflow:hidden;box-shadow:0 40px 110px rgba(15,122,58,0.18)}
        .re-cta:before{content:'';position:absolute;left:-12%;top:-18%;width:64%;height:240%;background:radial-gradient(circle at 30% 30%, rgba(255,255,255,0.06), transparent 30%);transform:rotate(25deg);opacity:0.14}
        .re-cta-inner{text-align:center;max-width:980px;margin:0 auto;position:relative;z-index:2}
        .re-cta h2{font-size:40px;font-weight:800;margin-bottom:14px;letter-spacing:0.2px}
        .re-cta p{font-size:18px;color:rgba(255,255,255,0.95);margin-bottom:26px}
        .re-btns .btn-donate{background:#fff;color:var(--green-1);border-radius:12px;padding:12px 28px;font-weight:800;border:none;box-shadow:0 14px 40px rgba(11,90,40,0.12)}
        .re-btns .btn-join{background:transparent;color:#fff;border:2px solid rgba(255,255,255,0.18);padding:12px 28px;border-radius:12px;font-weight:700}
        .re-cta h2::after{content:'';display:block;width:280px;height:70px;background:radial-gradient(circle, rgba(255,255,255,0.14), transparent 40%);margin:14px auto -58px;border-radius:50%;filter:blur(18px);opacity:0.95}
 
        .re-animate{opacity:0;transform:translateY(20px);transition:opacity 700ms ease, transform 700ms ease}
        .re-animate.in-view{opacity:1;transform:none}
      `}</style>
 
      <section className="re-section" aria-labelledby="re-about-heading">
        <div className="re-new-middle">
          <div className="re-wave" aria-hidden="true">
            <svg viewBox="0 0 1200 46" preserveAspectRatio="none"><path d="M0,30 C150,70 350,0 600,24 C850,48 1050,10 1200,30 L1200 0 L0 0 Z" fill="#eaf7ef" opacity="0.9"></path></svg>
          </div>
          {/* Section 1 — ABOUT */}
          <div className="re-about re-animate" role="region" aria-label="About Renewable Energy">
            <div className="re-about-inner">
              <div className="re-about-left">
                <span className="re-badge">Sustainable Development Since 1987</span>
                <h2 id="re-about-heading">Pioneering Sustainable Energy</h2>
                <div className="re-about-card">
                  <div className="re-accent-line" aria-hidden="true" />
                  <p>
                    Since 1987, Vikash has been a pioneer in promoting alternative energy sources. We provide advocacy and technical inputs on energy planning, promoting solutions like bio-gas plants and solar lights in Koraput, Keonjhar, and Puri. In 1989, we established a premier institution to advance the National Rural Energy Plan (NREP), furthering our commitment to sustainable energy.
                  </p>
                </div>
              </div>
 
              <div className="re-about-right">
                <div className="re-img-card" role="img" aria-label="Renewable energy project image">
                  <img src="/assets/images/Subsections/rn1.jpg" alt="Renewable energy project" />
                </div>
              </div>
            </div>
          </div>
 
          {/* Section 2 — PROGRAMS */}
          <div className="re-programs re-animate" role="region" aria-label="Our Sustainable Energy Programs">
            <div className="text-center mb-4">
              <h3 className="section-header" style={{fontSize:26,fontWeight:700}}>Our Sustainable Energy Programs</h3>
              <p className="mb-0" style={{color:'#356a42'}}>We focus on a range of solutions to provide clean and efficient energy to rural communities.</p>
            </div>
 
            <div className="re-programs-grid">
              {/* preserve exact program names */}
              <div className="re-card" role="article" aria-labelledby="p1">
                <div className="re-icon" aria-hidden="true"><div className="re-icon-inner">BG</div></div>
                <h5 id="p1">Bio-Gas Plant Installation</h5>
                <div className="re-divider" aria-hidden="true" />
                <div className="re-learn">Learn More →</div>
              </div>
 
              <div className="re-card" role="article" aria-labelledby="p2">
                <div className="re-icon" aria-hidden="true"><div className="re-icon-inner">SPV</div></div>
                <h5 id="p2">Solar Light & SPV System Promotion</h5>
                <div className="re-divider" aria-hidden="true" />
                <div className="re-learn">Learn More →</div>
              </div>
 
              <div className="re-card" role="article" aria-labelledby="p3">
                <div className="re-icon" aria-hidden="true"><div className="re-icon-inner">FS</div></div>
                <h5 id="p3">Fuel-Efficient Stoves</h5>
                <div className="re-divider" aria-hidden="true" />
                <div className="re-learn">Learn More →</div>
              </div>
 
              <div className="re-card" role="article" aria-labelledby="p4">
                <div className="re-icon" aria-hidden="true"><div className="re-icon-inner">WM</div></div>
                <h5 id="p4">Windmill Projects</h5>
                <div className="re-divider" aria-hidden="true" />
                <div className="re-learn">Learn More →</div>
              </div>
 
              <div className="re-card" role="article" aria-labelledby="p5">
                <div className="re-icon" aria-hidden="true"><div className="re-icon-inner">NREP</div></div>
                <h5 id="p5">Rural Energy Planning & Management (NREP)</h5>
                <div className="re-divider" aria-hidden="true" />
                <div className="re-learn">Learn More →</div>
              </div>
            </div>
          </div>
 
          {/* Section 3 — IMPACT HIGHLIGHT STRIP */}
          <div className="re-impact re-animate" role="region" aria-label="Impact highlights">
            <div className="re-impact-inner">
              <div className="re-stat" aria-live="polite">
                <div className="stat-icon" aria-hidden="true">⚡</div>
                <div className="num" data-target="1987">0</div>
                <p>1987 – Renewable Energy Initiatives Started</p>
              </div>
 
              <div className="re-stat" aria-live="polite">
                <div className="stat-icon" aria-hidden="true">🏘️</div>
                <div className="num" data-target="1200">0</div>
                <p>Rural Communities Served</p>
              </div>
 
              <div className="re-stat" aria-live="polite">
                <div className="stat-icon" aria-hidden="true">📋</div>
                <div className="num" data-target="5">0</div>
                <p>Sustainable Energy Programs</p>
              </div>
 
              <div className="re-stat" aria-live="polite">
                <div className="stat-icon" aria-hidden="true">🌱</div>
                <div className="num" data-target="250">0</div>
                <p>Clean Energy Solutions Promoted</p>
              </div>
            </div>
          </div>
 
          {/* Section 4 — POWER A GREENER FUTURE CTA (content preserved) */}
          <div className="re-cta re-animate" role="region" aria-label="Call to action: Power a Greener Future">
            <div className="re-cta-inner">
              <h2>Power a Greener Future</h2>
              <p>Your support helps us bring clean, renewable energy solutions to more communities in need.</p>
              <div className="d-flex justify-content-center gap-3 re-btns" style={{flexWrap:'wrap'}}>
                <Button className="btn-donate" onClick={openDonation}>Donate</Button>
                <Button className="btn-join" onClick={() => navigate('/interns')}>Join Us</Button>
              </div>
            </div>
          </div>
          <div className="re-wave" aria-hidden="true" style={{marginTop:'36px'}}>
            <svg viewBox="0 0 1200 60" preserveAspectRatio="none"><path d="M0,20 C200,80 400,0 600,36 C800,72 1000,8 1200,40 L1200 60 L0 60 Z" fill="#ffffff" opacity="0.95"></path></svg>
          </div>
        </div>
      </section>
 
      {/* Footer CTA retained below (unchanged footer will follow) */}
 
      <Footer />
 
      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};
 
export default RenewableEnergy;
 